/**
 * Bot skill level profiles for poker AI
 * Pure module — no Convex dependencies, no side effects
 */

export type BotSkillLevel = 'novice' | 'casual' | 'regular' | 'expert'

/** Game modes — must match Convex gameModeValidator exactly. Defined locally to keep this module pure. */
export type GameMode = 'practice' | 'private' | 'public' | 'community' | 'daily10'

export type BotModeStatus = 'active' | 'inactive'

/**
 * Feature flag: flip a mode from 'inactive' to 'active' to enable its
 * full per-mode bot mix. Inactive modes always return `count` copies of 'casual'.
 */
export const BOT_MODE_STATUS: Record<GameMode, BotModeStatus> = {
  practice: 'active',
  daily10: 'active',
  public: 'inactive',
  private: 'inactive',
  community: 'active'
}

export type BotProfile = {
  /** Probability of calling even without a good preflop hand */
  preflopCallThreshold: number
  /** Largest preflop loose-call price, in big blinds, allowed for leak-path calls.
   * Sized to cover a standard 3BB open so playable non-face hands (suited
   * connectors, T-high broadways) are never price-locked out of continuing;
   * how often they continue still follows preflopCallThreshold per skill. */
  maxLooseCallOpenSizeBb: number
  /** Largest preflop loose-call price, as a fraction of total stack, allowed for leak-path calls */
  maxLooseCallPctStack: number
  /** Whether the leak-path loose call is allowed after facing a re-raise/3-bet+ */
  allowLooseCallVsReraise: boolean
  /** Largest raise price, in big blinds, at which a priced-out face-card hand
   * (two face cards versus a re-raise or an oversized open) may still roll to
   * continue with the profile's looseness. Beyond this price two face cards
   * fold regardless of the roll. Stack pressure (maxLooseCallPctStack) applies
   * as well, so tight profiles effectively continue only versus cheap re-raises. */
  maxFaceCardContinueBb: number
  /** Open-raise size in big blinds for standard opens (no raise yet preflop).
   * Anchored on bb multiples, not pot fractions: the preflop pot is only 1.5bb,
   * so pot-fraction sizing collapses to the 2bb min-raise for every profile. */
  preflopOpenSizeBb: number
  /** Open-raise size in big blinds for premium hands (AA/KK/AK-class opens) */
  preflopOpenSizePremiumBb: number
  /** Multiplier on the computed re-raise amount (1.0 = standard) */
  preflopRaiseMultiplier: number
  /** Probability of calling a flop bet without a pair or draw (0 = always folds air, 1 = always calls) */
  flopLooseCallChance: number
  /** Minimum hand strength required to call a turn bet */
  turnCallMinStrength: 'any' | 'pair' | 'topPair' | 'twoPair'
  /** Minimum hand strength required to call a river bet */
  riverCallMinStrength: 'pair' | 'twoPair' | 'strong'
  /** Fraction of pot for strong made hands (flush, trips, two pair+) */
  betSizingStrong: number
  /** Fraction of pot for medium hands (top pair, draws) */
  betSizingMedium: number
  /** Fraction of pot for weak hands / bluffs */
  betSizingWeak: number
  /** Fraction of pot for bluff bets (turn/river) — higher = more polarized pressure */
  betSizingBluff: number
  /** Frequency of bluff-raises on turn and river [0–1] */
  bluffFrequency: number
  /** Continuation bet frequency on the flop [0–1] */
  cBetFrequency: number
  /** Frequency of value-betting the river when checked to with a strong hand [0–1] */
  riverValueBetFrequency: number
  /** Minimum hand strength required to value-bet the river */
  riverValueBetMinStrength: 'twoPair' | 'strong'
  /** Frequency of check-raising the flop with a monster hand [0–1] */
  flopCheckRaiseFrequency: number
  /** Frequency of firing a second barrel on the turn as aggressor without a strong hand [0–1] */
  turnBarrelFrequency: number
  /** Frequency of re-raising when aggressor holds an excellent hand and faces a bet [0–1] */
  aggressorRaiseFrequency: number
  /** Multiplier applied to pot-odds threshold when deciding calls (lower = looser calls) */
  potOddsDiscipline: number
}

export const BOT_PROFILES: Record<BotSkillLevel, BotProfile> = {
  novice: {
    preflopCallThreshold: 0.6,
    maxLooseCallOpenSizeBb: 4,
    maxLooseCallPctStack: 0.12,
    allowLooseCallVsReraise: false,
    maxFaceCardContinueBb: 12,
    preflopOpenSizeBb: 2.5,
    preflopOpenSizePremiumBb: 3,
    preflopRaiseMultiplier: 0.75,
    flopLooseCallChance: 0.25,
    turnCallMinStrength: 'pair',
    riverCallMinStrength: 'pair',
    betSizingStrong: 1.0,
    betSizingMedium: 0.75,
    betSizingWeak: 0.5,
    betSizingBluff: 0.5,
    bluffFrequency: 0.02,
    cBetFrequency: 0.85,
    riverValueBetFrequency: 0.3,
    riverValueBetMinStrength: 'strong',
    flopCheckRaiseFrequency: 0.0,
    turnBarrelFrequency: 0.4,
    aggressorRaiseFrequency: 0.0,
    potOddsDiscipline: 0.65
  },
  casual: {
    preflopCallThreshold: 0.35,
    maxLooseCallOpenSizeBb: 3.5,
    maxLooseCallPctStack: 0.1,
    allowLooseCallVsReraise: false,
    maxFaceCardContinueBb: 11,
    preflopOpenSizeBb: 3,
    preflopOpenSizePremiumBb: 3.5,
    preflopRaiseMultiplier: 0.9,
    flopLooseCallChance: 0.15,
    turnCallMinStrength: 'pair',
    riverCallMinStrength: 'pair',
    betSizingStrong: 0.85,
    betSizingMedium: 0.6,
    betSizingWeak: 0.4,
    betSizingBluff: 0.6,
    bluffFrequency: 0.05,
    cBetFrequency: 0.72,
    riverValueBetFrequency: 0.55,
    riverValueBetMinStrength: 'twoPair',
    flopCheckRaiseFrequency: 0.08,
    turnBarrelFrequency: 0.55,
    aggressorRaiseFrequency: 0.1,
    potOddsDiscipline: 0.8
  },
  regular: {
    preflopCallThreshold: 0.15,
    maxLooseCallOpenSizeBb: 3,
    maxLooseCallPctStack: 0.08,
    allowLooseCallVsReraise: false,
    maxFaceCardContinueBb: 10,
    preflopOpenSizeBb: 3,
    preflopOpenSizePremiumBb: 4,
    preflopRaiseMultiplier: 1.0,
    flopLooseCallChance: 0,
    turnCallMinStrength: 'pair',
    riverCallMinStrength: 'twoPair',
    betSizingStrong: 0.75,
    betSizingMedium: 0.5,
    betSizingWeak: 0.33,
    betSizingBluff: 0.75,
    bluffFrequency: 0.07,
    cBetFrequency: 0.68,
    riverValueBetFrequency: 0.75,
    riverValueBetMinStrength: 'twoPair',
    flopCheckRaiseFrequency: 0.2,
    turnBarrelFrequency: 0.65,
    aggressorRaiseFrequency: 0.25,
    potOddsDiscipline: 1.0
  },
  expert: {
    preflopCallThreshold: 0.05,
    maxLooseCallOpenSizeBb: 3,
    maxLooseCallPctStack: 0.06,
    allowLooseCallVsReraise: false,
    maxFaceCardContinueBb: 9,
    preflopOpenSizeBb: 3,
    preflopOpenSizePremiumBb: 4.5,
    preflopRaiseMultiplier: 1.1,
    flopLooseCallChance: 0,
    turnCallMinStrength: 'topPair',
    riverCallMinStrength: 'twoPair',
    betSizingStrong: 0.75,
    betSizingMedium: 0.5,
    betSizingWeak: 0.33,
    betSizingBluff: 1.0,
    bluffFrequency: 0.1,
    cBetFrequency: 0.62,
    riverValueBetFrequency: 0.9,
    riverValueBetMinStrength: 'twoPair',
    flopCheckRaiseFrequency: 0.35,
    turnBarrelFrequency: 0.75,
    aggressorRaiseFrequency: 0.4,
    potOddsDiscipline: 1.1
  }
}

export const ALL_SKILL_LEVELS = Object.keys(BOT_PROFILES) as BotSkillLevel[]

export type BotSkillTier = 'easy' | 'hard'
const BOT_SKILL_TIERS: Record<BotSkillTier, BotSkillLevel[]> = {
  easy: ['novice', 'casual'],
  hard: ['regular', 'expert']
}

export const DEFAULT_BOT_SKILL_LEVEL: BotSkillLevel = 'regular'

export function getBotProfile(skillLevel?: BotSkillLevel): BotProfile {
  return BOT_PROFILES[skillLevel ?? DEFAULT_BOT_SKILL_LEVEL]
}

/** Random skill level assignment — each bot gets an independently random level */
function randomSkill(skillLevels: BotSkillLevel[]): BotSkillLevel {
  return skillLevels[Math.floor(Math.random() * skillLevels.length)]
}

export function randomizeSkillLevels(botCount: number, skillLevels = ALL_SKILL_LEVELS): BotSkillLevel[] {
  return Array.from({ length: botCount }, () => randomSkill(skillLevels))
}

export function allSameSkillLevels(botCount: number, level: BotSkillLevel = 'casual'): BotSkillLevel[] {
  return Array.from({ length: botCount }, () => level)
}

/**
 * Deterministic skill level assignment seeded by a date string ("YYYY-MM-DD").
 * All players creating a daily10 game on the same date get the same bot skill levels.
 * Uses a simple LCG seeded by a djb2 hash of the date string.
 */
export function dateSeededSkillLevels(playedDate: string, botCount: number): BotSkillLevel[] {
  // djb2 hash of the date string → deterministic seed
  let seed = 5381
  for (let i = 0; i < playedDate.length; i++) {
    seed = ((seed << 5) + seed) ^ playedDate.charCodeAt(i)
    seed = seed >>> 0 // keep as 32-bit unsigned
  }
  // LCG: produces deterministic sequence from seed
  const next = (): number => {
    seed = (Math.imul(1664525, seed) + 1013904223) >>> 0
    return seed
  }
  return Array.from({ length: botCount }, () => ALL_SKILL_LEVELS[next() % ALL_SKILL_LEVELS.length])
}

/**
 * Return `count` bot skill levels for the given game mode.
 * Inactive modes return `count` copies of 'casual'.
 * Active modes delegate to the mode-specific seeding function.
 */
export function botSkillLevelsForMode(
  mode: GameMode,
  count: number,
  extraArgs?: { playedDate?: string; gameDifficulty?: BotSkillTier }
): BotSkillLevel[] {
  if (BOT_MODE_STATUS[mode] === 'inactive') {
    return allSameSkillLevels(count)
  }

  switch (mode) {
    case 'community': {
      const { gameDifficulty } = extraArgs ?? {}
      if (!gameDifficulty) throw new Error('botSkillLevelsForMode: community requires extraArgs.gameDifficulty')
      return randomizeSkillLevels(count, BOT_SKILL_TIERS[gameDifficulty])
    }
    case 'practice':
    case 'public':
    case 'private':
      return randomizeSkillLevels(count)
    case 'daily10': {
      const { playedDate } = extraArgs ?? {}
      if (!playedDate) throw new Error('botSkillLevelsForMode: daily10 requires extraArgs.playedDate')
      return dateSeededSkillLevels(playedDate, count)
    }
    default:
      return allSameSkillLevels(count)
  }
}
